<script lang="ts">
  import * as Menubar from "$lib/components/ui/menubar/index.js";
  // stores
  import { user, setDoLogin, doLogin } from "$lib/stores/auth";
  import { ui } from "$lib/stores/ui"
  // components
  import Login from "./Login.svelte"
  import Import from "./Import.svelte"
  import StampEditor from "./editors/StampEditor.svelte";
  import SetEditor from "./editors/SetEditor.svelte";
  import BlockEditor from "./editors/BlockEditor.svelte";
  import BookletEditor from "./editors/BookletEditor.svelte";
  import SeTenantsEditor from "./editors/SeTenantsEditor.svelte";
  import UserDashboard from "./UserDashboard.svelte";
  import RegisterUserDialog from "./RegisterUserDialog.svelte";
  import ManualDialog from "./ManualDialog.svelte";
  import HelpDialog from "./HelpDialog.svelte";
  import ItemAndSuggestionLists from "./itemlist/ItemAndSuggestionLists.svelte";

  import icon from './img/postilio_icon.svg?url'

  import { downloadAllItems } from "$lib/utils/download"
  import { login } from "$lib/services/auth";
  import { postItemSuggestion, deleteAll, getAllItems, getAllSuggestions } from "$lib/services/philatelic_items"

  import { type SingleStamp, type PhilatelicSet, type Block, type Booklet, type SeTenants, type WithoutIdAndReview } from "@briefmarken_katalog/types";

  import { StampEditorModel } from "./editors/models/StampEditorModel.svelte";
  import { SetEditorModel } from "./editors/models/SetEditorModel.svelte";
  import { BlockEditorModel } from "./editors/models/BlockEditorModel.svelte";
  import { BookletEditorModel } from "./editors/models/BookletEditorModel.svelte";
  import { SeTenantsEditorModel } from "./editors/models/SeTenantsEditorModel.svelte";

  import { overlay } from "$lib/stores/ui";

  import { onMount } from 'svelte';
  import { connectSse } from '$lib/services/sse';

  import { t, translate, type TranslationKey } from "$lib/i18n";
  import LanguageSelector from "$lib/i18n/LanguageSelector.svelte";
  import Switch from "$lib/components/ui/switch/switch.svelte";
  import CatalogView from "./itemlist/CatalogView.svelte";
  import OrientationGuard from "./OrientationGuard.svelte";
  import { catalogMode } from "$lib/stores/ui";
  import { isMobileDevice } from "$lib/stores/device";

  import './app.css';

  setDoLogin(async () => {
    ui.open('login');
    await user.waitForUpdate();
    return $user!.token;
  });

  // Hoisted so bind:model works (Svelte 5 ownership). async function declarations are hoisted.
  let stampEditorModel = $state(new StampEditorModel({ onCreate: postSingleStampSuggestion }));
  let setEditorModel = $state(new SetEditorModel({ onCreate: postPhilatelicSetSuggestion }));
  let blockEditorModel = $state(new BlockEditorModel({ onCreate: postBlockSuggestion }));
  let bookletEditorModel = $state(new BookletEditorModel({ onCreate: postBookletSuggestion }));
  let seTenantsEditorModel = $state(new SeTenantsEditorModel({ onCreate: postSeTenantsSuggestion }));

  onMount(() => connectSse());

  let _prevRole = $state<string | null | undefined>(undefined);
  $effect(() => {
    if ($isMobileDevice) {
      // Mobile clients are locked to catalog mode regardless of role.
      catalogMode.set(true);
      _prevRole = undefined; // re-apply role logic if the device stops being "mobile"
      return;
    }
    const role = $user?.role ?? null;
    if (role === _prevRole) return;
    _prevRole = role;
    if (role === 'Viewer') catalogMode.set(true);
    else if (role !== null) catalogMode.set(false);
  });

  async function postSingleStampSuggestion(item: WithoutIdAndReview<SingleStamp>)
  {
    try {
      await postItemSuggestion(item, doLogin);
    }
    catch (e: any)
    {
      alert(translate('errors.postStampSuggestion') + e.message);
    }
  }

  async function postPhilatelicSetSuggestion(set: WithoutIdAndReview<PhilatelicSet>)
  {
    try {
      await postItemSuggestion(set, doLogin);
    }
    catch (e: any)
    {
      alert(translate('errors.postSetSuggestion') + e.message);
    }
  }

  async function postBlockSuggestion(block: WithoutIdAndReview<Block>)
  {
    try {
      await postItemSuggestion(block, doLogin);
    }
    catch (e: any)
    {
      alert(translate('errors.postBlockSuggestion') + e.message);
    }
  }

  async function postBookletSuggestion(booklet: WithoutIdAndReview<Booklet>)
  {
    try {
      await postItemSuggestion(booklet, doLogin);
    }
    catch (e: any)
    {
      alert(translate('errors.postBookletSuggestion') + e.message);
    }
  }

  async function postSeTenantsSuggestion(seTenants: WithoutIdAndReview<SeTenants>)
  {
    try {
      await postItemSuggestion(seTenants, doLogin);
    }
    catch (e: any)
    {
      alert(translate('errors.postSeTenantsSuggestion') + e.message);
    }
  }

  async function dumpToConsole()
  {
    try {
      const items = await getAllItems(doLogin);
      const suggestions = await getAllSuggestions(doLogin);
      console.log('items:', items);
      console.log('suggestions:', suggestions);
    }
    catch (e: any)
    {
      alert(translate('errors.dumpToConsole') + e.message);
    }
  }

  async function deleteAllPhilatelicItemsAndSuggestions()
  {
    try {
      await deleteAll(doLogin);
    }
    catch (e: any)
    {
      alert(translate('errors.deleteAll') + e.message);
    }
  }

</script>

<!-- top application menu -->
<div class="page-container">
  <OrientationGuard />
  <Menubar.Root class="fixed-header">
    <img class='icon' src={icon} alt='icon'/>
    <!-- Suggest -->
    {#if !$isMobileDevice && $user && ($user.role === "Admin" || $user.role === "Reviewer" || $user.role === "Contributor")}
    <Menubar.Menu>
      <Menubar.Trigger disabled={$catalogMode}>{$t('menu.suggest')}</Menubar.Trigger>
      <Menubar.Content>
        <Menubar.Item onSelect={()=>ui.open('stampEditor')}>{$t('menu.stamp')}</Menubar.Item>
        <Menubar.Item onSelect={()=>ui.open('setEditor')}>{$t('menu.set')}</Menubar.Item>
        <Menubar.Item onSelect={()=>ui.open('blockEditor')}>{$t('menu.block')}</Menubar.Item>
        <Menubar.Item onSelect={()=>ui.open('bookletEditor')}>{$t('menu.booklet')}</Menubar.Item>
        <Menubar.Item onSelect={()=>ui.open('seTenantsEditor')}>{$t('menu.seTenants')}</Menubar.Item>
      </Menubar.Content>
    </Menubar.Menu>
    {/if}

    <!-- Admin -->
    {#if !$isMobileDevice && $user && $user.role=="Admin"}
    <Menubar.Menu>
      <Menubar.Trigger>{$t('menu.admin')}</Menubar.Trigger>
      <Menubar.Content>
        <Menubar.Item onSelect={()=>ui.open('userDashboard')}>{$t('menu.userDashboard')}</Menubar.Item>
        <Menubar.Item onSelect={()=>ui.open('registerUser')}>{$t('registerUser.menuItem')}</Menubar.Item>
        <Menubar.Separator />
        <Menubar.Item onSelect={deleteAllPhilatelicItemsAndSuggestions}>{$t('menu.deleteDatabase')}</Menubar.Item>
        <Menubar.Item onSelect={downloadAllItems.bind(null, $user.token, undefined)}>{$t('menu.exportAdmin')}</Menubar.Item>
        <Menubar.Item onSelect={()=>ui.open('import')}>{$t('menu.importAdmin')}</Menubar.Item>
        <Menubar.Item onSelect={dumpToConsole}>{$t('menu.dumpToConsole')}</Menubar.Item>
      </Menubar.Content>
    </Menubar.Menu>
    {/if}

    <!-- Help -->
    <button
      class="focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground outline-hidden flex select-none items-center rounded-sm px-2 py-1 text-sm font-medium"
      onclick={() => ui.open('manual')}
    >{$t('menu.help')}</button>

    <!-- Spacer -->
    <div class="flex-1"></div>

    <!-- Mode switch (hidden for Viewers and on mobile devices) -->
    {#if !$isMobileDevice && !($user?.role === 'Viewer')}
    <div class="flex items-center gap-1.5 px-2 border-l border-r border-gray-300">
      <span class="text-xs select-none {!$catalogMode ? 'font-bold text-gray-800' : 'text-gray-400'}">{$t('mode.creatorMode')}</span>
      <Switch bind:checked={$catalogMode} />
      <span class="text-xs select-none {$catalogMode ? 'font-bold text-gray-800' : 'text-gray-400'}">{$t('mode.catalogMode')}</span>
    </div>
    {/if}

    <!-- Language selector -->
    <LanguageSelector />

    <!-- User -->
    <Menubar.Menu>
      <Menubar.Trigger class="max-sm:max-w-[40vw] max-sm:truncate max-sm:inline-block
      {$user ? 'bg-[darkseagreen] hover:bg-[darkseagreen] data-[state=closed]:bg-[darkseagreen] data-[state=open]:bg-[darkseagreen]'
      : 'bg-[rosybrown] hover:bg-[rosybrown] data-[state=closed]:bg-[rosybrown] data-[state=open]:bg-[rosybrown]'}"
      >{$user ? ($user.name ?? $user.email) : $t('menu.login')}</Menubar.Trigger>

      <Menubar.Content>

        {#if !$user}
        <Menubar.Item onSelect={()=>ui.open('login')}>
          {$t('menu.login')}
        </Menubar.Item>
        {/if}

        {#if $user}
        <div class="user_details">
          {#if $user.name}<span style="font-weight: bold;">{$user.name}</span><br>{/if}
          {$user.email}<br>
          {$t('menu.role')}<span style="font-weight: bold;">{$t(`roles.${$user.role}` as TranslationKey)}</span>
        </div>
        <Menubar.Separator />
        <Menubar.Item onSelect={() => ui.open('login')}>
          {$t('menu.changeUser')}
        </Menubar.Item>
        {/if}

      </Menubar.Content>
    </Menubar.Menu>
  </Menubar.Root>
  <div class="scrollable-content">
  <!-- overlays -->
    <Login onLogin={login}></Login>
    <ManualDialog />
    <HelpDialog />
    {#if $catalogMode}
      <CatalogView />
    {:else if $user}
    <StampEditor bind:model={stampEditorModel} isOpen={overlay("stampEditor")}/>
    <SetEditor bind:model={setEditorModel} isOpen={overlay("setEditor")}/>
    <BlockEditor bind:model={blockEditorModel} isOpen={overlay("blockEditor")}/>
    <BookletEditor bind:model={bookletEditorModel} isOpen={overlay("bookletEditor")}/>
    <SeTenantsEditor bind:model={seTenantsEditorModel} isOpen={overlay("seTenantsEditor")}/>
    <UserDashboard/>
    <ItemAndSuggestionLists/>
    <Import/>
    <RegisterUserDialog/>
    {/if}
  </div>
</div>

<style>
  .icon {
    width: 45px;
  }
</style>